import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  MenuItem,
  Stack,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'src/redux/hooks';
import ComplaintsService, {
  ComplaintThreadDetail,
  ComplaintMessage,
  ComplaintStatus,
  ComplaintMessageSource
} from 'src/services/complaints-service';

const ComplaintThreadDetailPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const user = useAppSelector((s) => s.user);
  const complaintsService = ComplaintsService.getInstance();

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [thread, setThread] = useState<ComplaintThreadDetail | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadThread = useCallback(async () => {
    if (!threadId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await complaintsService.getThreadDetails(threadId);
      setThread(data);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Failed to load thread:', err);
      setError('Failed to load conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [threadId, complaintsService]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !threadId) {
      console.log('Missing required fields:', { newMessage: newMessage.trim(), threadId, userId: user.user?.id });
      return;
    }

    const userId = user.user?.id;
    if (!userId) {
      setError('User ID not found. Please refresh the page and try again.');
      console.error('User ID is missing:', user);
      return;
    }

    try {
      setSending(true);
      setError(null);
      console.log('Sending message:', { threadId, message: newMessage.trim(), userId });
      const updatedThread = await complaintsService.sendMessage(
        threadId,
        newMessage.trim(),
        userId
      );
      setThread(updatedThread);
      setNewMessage('');
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus: ComplaintStatus) => {
    if (!threadId) return;

    try {
      setUpdatingStatus(true);
      setError(null);
      await complaintsService.updateThreadStatus(threadId, newStatus);
      // Reload thread to get updated status
      await loadThread();
    } catch (err) {
      console.error('Failed to update status:', err);
      setError('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!thread) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Complaint thread not found.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/complaints')}
          sx={{ mt: 2 }}
        >
          Back to Complaints
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate('/complaints')}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>
              {thread.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Conversation with {thread.tenantName}
            </Typography>
          </Box>
          <IconButton onClick={loadThread} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Status and metadata */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={complaintsService.getStatusLabel(thread.status)}
            color={complaintsService.getStatusColor(thread.status)}
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="caption" color="text.secondary">
            Created: {new Date(thread.createdAt).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last Activity: {new Date(thread.lastActivityAt).toLocaleString()}
          </Typography>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Status Update */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Update Status
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant={thread.status === ComplaintStatus.Open ? 'contained' : 'outlined'}
              color="error"
              disabled={updatingStatus || thread.status === ComplaintStatus.Open}
              onClick={() => handleStatusChange(ComplaintStatus.Open)}
            >
              Open
            </Button>
            <Button
              size="small"
              variant={thread.status === ComplaintStatus.InProgress ? 'contained' : 'outlined'}
              color="warning"
              disabled={updatingStatus || thread.status === ComplaintStatus.InProgress}
              onClick={() => handleStatusChange(ComplaintStatus.InProgress)}
            >
              In Progress
            </Button>
            <Button
              size="small"
              variant={thread.status === ComplaintStatus.Resolved ? 'contained' : 'outlined'}
              color="success"
              disabled={updatingStatus || thread.status === ComplaintStatus.Resolved}
              onClick={() => handleStatusChange(ComplaintStatus.Resolved)}
            >
              Resolved
            </Button>
            <Button
              size="small"
              variant={thread.status === ComplaintStatus.Closed ? 'contained' : 'outlined'}
              // color="default"
              disabled={updatingStatus || thread.status === ComplaintStatus.Closed}
              onClick={() => handleStatusChange(ComplaintStatus.Closed)}
            >
              Closed
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card sx={{ mb: 3, maxHeight: '60vh', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          {thread.messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No messages yet
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {thread.messages.map((message: ComplaintMessage) => {
                const isAdmin = message.source === ComplaintMessageSource.Admin;
                const isRead = isAdmin ? message.isReadByAdmin : message.isReadByTenant;

                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      flexDirection: isAdmin ? 'row-reverse' : 'row',
                      gap: 2,
                      alignItems: 'flex-start'
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: isAdmin
                          ? theme.palette.primary.main
                          : alpha('#4CAF50', 0.2),
                        color: isAdmin ? 'white' : '#4CAF50',
                        width: 40,
                        height: 40
                      }}
                    >
                      {isAdmin ? <AdminIcon /> : <PersonIcon />}
                    </Avatar>
                    <Paper
                      elevation={1}
                      sx={{
                        p: 2,
                        maxWidth: '70%',
                        bgcolor: isAdmin
                          ? theme.palette.primary.main
                          : theme.palette.background.paper,
                        color: isAdmin ? 'white' : 'text.primary',
                        borderRadius: 1,
                        borderTopRightRadius: isAdmin ? 0 : 1,
                        borderTopLeftRadius: isAdmin ? 1 : 0
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 0.5 }}>
                        {message.senderName}
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.body}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.7, display: 'block', mt: 1, textAlign: 'right' }}
                      >
                        {new Date(message.sentAt).toLocaleString()}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </CardContent>

        <Divider />

        {/* Message Input */}
        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending || thread.status === ComplaintStatus.Closed}
              size="small"
            />
            <Button
              variant="contained"
              endIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim() || thread.status === ComplaintStatus.Closed}
              sx={{ minWidth: 100 }}
            >
              Send
            </Button>
          </Stack>
          {thread.status === ComplaintStatus.Closed && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This complaint is closed. Reopen it to send messages.
            </Typography>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default ComplaintThreadDetailPage;
